import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin, Gem, Fingerprint, ShieldCheck, Sparkles, Globe,
  FileText, Download, QrCode, Layers,
  Mountain, Cog, Database, Truck, Leaf, Sprout, Award, ChevronRight,
} from "lucide-react";
import { StatusBadge } from "@/components/ui-bits";
import { API } from "@/lib/api";

const STAGE_ICONS = {
  source: MapPin,
  rough_registration: FileText,
  custody: Layers,
  cutting_polishing: Gem,
  polished_identity: Fingerprint,
  certificate: ShieldCheck,
};

const STATUS_LEGEND = [
  ["Submitted", "Received but not yet reviewed"],
  ["Reviewed", "Checked for completeness and consistency"],
  ["Provider-confirmed", "Confirmed by the named laboratory or technology provider"],
  ["Identity-matched", "The physical diamond matches its captured identity"],
  ["Verified", "All required evidence for that claim has been completed"],
  ["Pending", "Required evidence remains incomplete"],
  ["Suspended", "The record is under investigation"],
  ["Revoked", "The certificate is no longer valid"],
];

const TABS = [
  ["identity", "Physical Identity"],
  ["reports", "Independent Reports"],
  ["providers", "Connected Records"],
  ["audit", "Audit History"],
];

const ITRACEIT_STEPS = [
  [Mountain, "Provenance"],
  [Cog, "Transformation"],
  [Database, "Product Data"],
  [Truck, "Chain of Custody"],
  [Leaf, "ESG Indicators"],
  [Sprout, "Sustainability Reports"],
  [Award, "Certificates"],
];

function ITraceItFlow() {
  return (
    <div data-testid="itraceit-flow" className="mt-4 rounded-xl border border-white/10 bg-[#0b1728] p-6">
      <div className="label-eyebrow mb-6">Traceability by iTraceiT</div>
      <div className="flex flex-wrap items-start justify-between gap-y-6">
        {ITRACEIT_STEPS.map(([Icon, label], i) => (
          <React.Fragment key={label}>
            <div className="flex flex-col items-center text-center w-[80px]">
              <div className="grid place-items-center h-12 w-12 rounded-full border border-bb-gold/40 bg-bb-gold/[0.06]">
                <Icon className="h-5 w-5 text-bb-gold" strokeWidth={1.1} />
              </div>
              <span className="mt-2.5 text-[0.6rem] leading-tight tracking-wide text-bb-silver/70">{label}</span>
            </div>
            {i < ITRACEIT_STEPS.length - 1 && (
              <ChevronRight className="hidden sm:block h-4 w-4 text-bb-gold/30 mt-4 shrink-0" strokeWidth={1.6} />
            )}
          </React.Fragment>
        ))}
      </div>
      <p className="mt-7 text-center text-sm text-bb-silver/70 leading-relaxed">
        If it defines your product, <span className="text-bb-white">we can capture it and protect it.</span>
      </p>
    </div>
  );
}

const fmtDate = (v) =>
  v ? new Date(v).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }) : null;

function stageButtonClass(isActive, notSelected) {
  if (isActive) return "border-bb-gold bg-bb-gold/15 shadow-[0_0_22px_rgba(198,168,124,0.4)]";
  if (notSelected) return "border-white/8 bg-transparent opacity-40";
  return "border-white/12 bg-white/[0.03] group-hover:border-bb-gold/50";
}

export default function CertificateViewer({ cert }) {
  const [active, setActive] = useState(0);
  const [tab, setTab] = useState("identity");
  if (!cert) return null;
  const journey = cert.journey || [];
  const stage = journey[active] || {};

  return (
    <div data-testid="certificate-viewer" className="grid gap-8 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <SummaryCard cert={cert} />
      </div>
      <div className="lg:col-span-7 space-y-6">
        <JourneyCard journey={journey} active={active} setActive={setActive} stage={stage} />
        <StatusLegendCard />
        <DetailTabs cert={cert} tab={tab} setTab={setTab} />
      </div>
    </div>
  );
}

function SummaryCard({ cert }) {
  const pi = cert.physical_identity || {};
  return (
    <div className="glass rounded-2xl overflow-hidden">
      <div className="relative aspect-square bg-gradient-to-b from-[#0A1A30] to-black flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 radial-blue" />
        <img
          src={(cert.media?.images || [])[0]}
          alt={cert.name}
          style={{ mixBlendMode: "screen" }}
          className="relative z-10 w-3/4 h-3/4 object-contain animate-float drop-shadow-[0_0_60px_rgba(198,168,124,0.25)]"
        />
        <div className="absolute top-4 left-4 z-20">
          <StatusBadge status={cert.status} label={cert.status_label} testid="cert-status-badge" />
        </div>
      </div>
      <div className="p-6">
        <div className="flex items-center gap-2.5">
          <img src="/logo-mark.png" alt="" className="h-7 w-auto" />
          <div className="label-eyebrow">Botswana Brilliance Certificate</div>
        </div>
        <h3 className="font-serif text-3xl mt-3">{cert.name}</h3>
        <div className="mt-1 font-mono text-sm text-bb-gold tracking-wider">{cert.bb_number}</div>
        {cert.updated_at && (
          <div data-testid="cert-issued-date" className="mt-1 text-xs text-bb-silver/50">
            Issued {fmtDate(cert.updated_at)}
          </div>
        )}
        {cert.pride_mark?.applied && (
          <div data-testid="pride-mark-badge" className="mt-3 inline-flex items-center gap-2 rounded-full border border-bb-gold/40 bg-bb-gold/10 px-3.5 py-1.5">
            <Sparkles className="h-3.5 w-3.5 text-bb-gold" strokeWidth={1.3} />
            <span className="text-xs text-bb-gold tracking-wide">Botswana Pride Mark applied · {cert.pride_mark.engraving_id}</span>
          </div>
        )}
        {(cert.original_rough_id || cert.rough_verification_level) && (
          <div className="mt-4 rounded-lg border border-white/8 bg-white/[0.02] p-3 space-y-1">
            {cert.rough_verification_level && (
              <div className="text-xs"><span className="text-bb-silver/50">Rough-to-polished: </span><span className="text-bb-gold">{cert.rough_verification_level}</span></div>
            )}
            {cert.original_rough_id && (
              <div className="text-xs"><span className="text-bb-silver/50">Original rough ID: </span><span className="font-mono text-bb-silver/85">{cert.original_rough_id}</span></div>
            )}
            {cert.manufactured_where && (
              <div className="text-xs"><span className="text-bb-silver/50">Manufactured: </span><span className="text-bb-silver/85">{cert.manufactured_where}</span></div>
            )}
          </div>
        )}

        <div className="mt-5 grid grid-cols-3 gap-3 text-center">
          {[["Carat", `${pi.carat}`], ["Colour", pi.color], ["Clarity", pi.clarity]].map(([k, v]) => (
            <div key={k} className="rounded-lg border border-white/8 bg-white/[0.02] py-3">
              <div className="font-serif text-2xl text-bb-white">{v}</div>
              <div className="text-[0.62rem] uppercase tracking-[0.2em] text-bb-silver/50 mt-1">{k}</div>
            </div>
          ))}
        </div>

        <div className="mt-5 flex items-center gap-3">
          <div className="grid place-items-center h-14 w-14 rounded-lg border border-white/10 bg-white/[0.03]">
            <QrCode className="h-8 w-8 text-bb-silver" strokeWidth={1} />
          </div>
          <div className="text-xs text-bb-silver/60 leading-relaxed">
            The QR code opens the official live certificate, the authoritative record and not a static copy.
          </div>
        </div>

        <a
          href={`${API}/certificate/${cert.bb_number}/pdf`}
          target="_blank"
          rel="noreferrer"
          data-testid="download-pdf-btn"
          className="mt-5 btn-ghost rounded-full w-full py-3 text-sm flex items-center justify-center gap-2"
        >
          <Download className="h-4 w-4" strokeWidth={1.3} /> Download PDF snapshot
        </a>
      </div>
    </div>
  );
}

function JourneyCard({ journey, active, setActive, stage }) {
  const metaRows = useMemo(
    () =>
      [
        ["Source", stage.provider],
        ["Recorded by", stage.supplied_by],
        ["Verification method", stage.verification_method],
        ["Date recorded", fmtDate(stage.date)],
        ["Reference", stage.reference],
        ["Visibility", stage.visibility],
      ].filter(([, v]) => v),
    [stage]
  );

  return (
    <div className="glass rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-5">
        <Layers className="h-4 w-4 text-bb-gold" strokeWidth={1.2} />
        <span className="label-eyebrow">The Six-Stage Journey</span>
      </div>
      <div className="grid grid-cols-6 gap-2">
        {journey.map((s, i) => {
          const Icon = STAGE_ICONS[s.key] || MapPin;
          const isActive = i === active;
          const notSelected = s.status === "not_selected";
          return (
            <button
              key={s.key}
              data-testid={`journey-stage-${s.key}`}
              onClick={() => setActive(i)}
              className="group relative flex flex-col items-center"
            >
              <div className={`grid place-items-center h-11 w-11 rounded-full border transition-all duration-500 ${stageButtonClass(isActive, notSelected)}`}>
                <Icon className={isActive ? "text-bb-gold" : "text-bb-silver"} size={18} strokeWidth={1.2} />
              </div>
              <span className={`mt-2 text-[0.58rem] text-center leading-tight ${isActive ? "text-bb-gold" : "text-bb-silver/50"}`}>
                {s.title}
              </span>
            </button>
          );
        })}
      </div>

      <div className="hairline my-5" />

      <AnimatePresence mode="wait">
        <motion.div key={active} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.4 }}>
          <div className="flex items-center justify-between gap-3">
            <h4 className="font-serif text-2xl">{stage.title}</h4>
            {stage.status && <StatusBadge status={stage.status} label={stage.status} testid={`stage-status-${stage.key}`} />}
          </div>
          <p className="text-sm text-bb-silver/70 mt-1 mb-4">{stage.summary}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
            {metaRows.map(([k, v]) => (
              <div key={k} className="rounded-lg border border-white/8 bg-white/[0.015] px-3 py-2">
                <div className="text-[0.58rem] uppercase tracking-[0.16em] text-bb-silver/40">{k}</div>
                <div className="text-xs text-bb-white mt-0.5">{v}</div>
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {(stage.evidence || []).map((e, idx) => (
              <div key={`${e.label}-${idx}`} className="rounded-lg border border-white/8 bg-white/[0.02] px-4 py-3">
                <div className="text-[0.62rem] uppercase tracking-[0.18em] text-bb-silver/45">{e.label}</div>
                <div className="text-sm text-bb-white mt-1">{e.value}</div>
              </div>
            ))}
          </div>
          {stage.document && (
            <div className="mt-4" data-testid={`stage-document-${stage.key}`}>
              <div className="label-eyebrow flex items-center gap-2 mb-3">
                <FileText className="h-3.5 w-3.5 text-bb-gold" strokeWidth={1.4} />
                {stage.document.name}{stage.document.ref ? ` · ${stage.document.ref}` : ""}
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                {(stage.document.items || []).map(([k, v]) => (
                  <div key={k} className="rounded-lg border border-white/8 bg-white/[0.02] px-4 py-3">
                    <div className="text-[0.62rem] uppercase tracking-[0.18em] text-bb-silver/45">{k}</div>
                    <div className="text-sm text-bb-white mt-1">{v}</div>
                  </div>
                ))}
              </div>
              {stage.document.viewer_url && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="label-eyebrow">Live Sarine viewer</span>
                    <a href={stage.document.viewer_url} target="_blank" rel="noreferrer" className="text-xs text-bb-gold hover:underline">Open in new tab</a>
                  </div>
                  <div className="rounded-xl overflow-hidden border border-white/10 bg-black/40">
                    <iframe src={stage.document.viewer_url} title={stage.document.name} className="w-full h-[620px]" allow="fullscreen" />
                  </div>
                </div>
              )}
            </div>
          )}
          {stage.key === "custody" && <ITraceItFlow />}
          {stage.powered_by && (
            <div data-testid={`stage-powered-${stage.key}`} className="mt-4 flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.22em] text-bb-silver/40">
              <span className="h-1 w-1 rounded-full bg-bb-gold/60" />
              Powered by {stage.powered_by}
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function StatusLegendCard() {
  return (
    <div data-testid="status-legend" className="glass rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck className="h-4 w-4 text-bb-gold" strokeWidth={1.2} />
        <span className="label-eyebrow">What each status means</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
        {STATUS_LEGEND.map(([label, meaning]) => (
          <div key={label} className="flex items-start gap-3">
            <StatusBadge status={label} label={label} />
            <span className="text-xs text-bb-silver/60 leading-relaxed flex-1">{meaning}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DetailTabs({ cert, tab, setTab }) {
  return (
    <div className="glass rounded-2xl p-6">
      <div className="flex flex-wrap gap-2 mb-5">
        {TABS.map(([k, label]) => (
          <button
            key={k}
            data-testid={`cert-tab-${k}`}
            onClick={() => setTab(k)}
            className={`rounded-full px-4 py-1.5 text-xs tracking-wide transition-all duration-300 ${
              tab === k ? "bg-bb-gold text-black" : "border border-white/12 text-bb-silver/70 hover:border-bb-gold/40"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "identity" && <IdentityTab cert={cert} />}
      {tab === "reports" && <ReportsTab cert={cert} />}
      {tab === "providers" && <ProvidersTab cert={cert} />}
      {tab === "audit" && <AuditTab cert={cert} />}
    </div>
  );
}

function IdentityTab({ cert }) {
  const pi = cert.physical_identity || {};
  const fields = {
    Shape: pi.shape, Cut: pi.cut, Polish: pi.polish, Symmetry: pi.symmetry,
    Measurements: pi.measurements, Fluorescence: pi.fluorescence,
    Brightness: cert.light_performance?.brightness, Fire: cert.light_performance?.fire,
  };
  return (
    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
      {Object.entries(fields).map(([k, v]) => (
        <div key={k} className="flex justify-between border-b border-white/6 py-2">
          <span className="text-sm text-bb-silver/55">{k}</span>
          <span className="text-sm text-bb-white">{v || "N/A"}</span>
        </div>
      ))}
    </div>
  );
}

function ReportsTab({ cert }) {
  return (
    <div className="space-y-3">
      {(cert.lab_reports || []).map((r) => (
        <div key={`${r.provider}-${r.number}`} className="flex items-center justify-between rounded-lg border border-white/8 bg-white/[0.02] px-4 py-3">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-bb-gold" strokeWidth={1.2} />
            <div>
              <div className="text-sm text-bb-white">{r.provider} · {r.type}</div>
              <div className="text-xs text-bb-silver/50 font-mono">No. {r.number}</div>
            </div>
          </div>
          <StatusBadge status={r.status} label={r.status} />
        </div>
      ))}
      <p className="text-xs text-bb-silver/45 pt-1">
        Botswana Brilliance verifies identity and origin records. Independent laboratory grading is performed by the
        laboratories named above and remains their own assessment.
      </p>
    </div>
  );
}

function ProvidersTab({ cert }) {
  return (
    <div className="space-y-2">
      {(cert.providers || []).map((p) => (
        <div key={p.name} className="flex items-center justify-between rounded-lg border border-white/8 bg-white/[0.02] px-4 py-3">
          <span className="text-sm text-bb-silver/85">{p.name}</span>
          <span className="rounded-full border border-white/12 px-3 py-1 text-[0.62rem] uppercase tracking-[0.15em] text-bb-silver/55">{p.status}</span>
        </div>
      ))}
      <p className="text-xs text-bb-silver/45 pt-2">
        The status shown reflects how each record relates to this diamond. Integrated, accepted, available on request or
        proposed does not imply a formal partnership unless explicitly labelled Formal partner. Not every provider applies to every diamond.
      </p>
    </div>
  );
}

function AuditTab({ cert }) {
  return (
    <div className="space-y-0">
      {(cert.audit || []).map((a, i) => (
        <div key={`${a.event}-${i}`} className="flex gap-4 py-2.5 border-b border-white/6">
          <span className="text-xs text-bb-silver/45 font-mono whitespace-nowrap w-24">{fmtDate(a.date)}</span>
          <span className="text-sm text-bb-white flex-1">{a.event}</span>
          <span className="text-xs text-bb-silver/40">{a.actor}</span>
        </div>
      ))}
    </div>
  );
}
